import { useEffect, useRef } from "react";

export default function PaymentModal({ open, onClose, tutorName }) {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    window.setTimeout(() => closeRef.current?.focus(), 30);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-modal-title"
        className="dark-shell relative w-full max-w-2xl p-6 sm:p-8"
      >
        <button
          ref={closeRef}
          type="button"
          className="absolute right-4 top-4 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-sm font-semibold text-white/80"
          onClick={onClose}
        >
          Close
        </button>

        <div className="space-y-4">
          <div className="eyebrow">Payment Placeholder</div>
          <h3 id="payment-modal-title" className="font-display text-3xl font-bold text-white">
            Payment integration options for {tutorName}
          </h3>
          <p className="text-base leading-7 text-white/70">
            This starter keeps payment UI safe by describing the real integration path without
            storing secrets in the client. Use the included Cashfree serverless examples for
            Vercel, Netlify, or an Express service.
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="glow-card p-5">
              <p className="font-display text-xl font-semibold text-white">Cashfree Order API</p>
              <p className="mt-2 text-sm leading-6 text-white/70">
                Create orders securely on the server with <code>CASHFREE_APP_ID</code> and{" "}
                <code>CASHFREE_SECRET</code>.
              </p>
            </div>
            <div className="glow-card p-5">
              <p className="font-display text-xl font-semibold text-white">UPI / Payment Links</p>
              <p className="mt-2 text-sm leading-6 text-white/70">
                Pair the booking record with a counselor-generated UPI link or shared payment
                link.
              </p>
            </div>
            <div className="glow-card p-5">
              <p className="font-display text-xl font-semibold text-white">Deployment</p>
              <p className="mt-2 text-sm leading-6 text-white/70">
                Ready-to-copy serverless templates are included under <code>serverless/</code>.
              </p>
            </div>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/10 p-5 text-sm leading-7 text-white/70">
            Production checklist:
            <ul className="mt-2 list-disc pl-5">
              <li>Store secrets only in server-side environment variables.</li>
              <li>Create the order server-side and return only a safe payload to the browser.</li>
              <li>Verify payment status with webhooks before unlocking paid content.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
